/**
 * CenterToggle Component
 *
 * A toggle switch control for enabling/disabling center customization
 * on QR codes. When enabled, reveals additional customization options
 * for the center overlay area.
 *
 * Requirements:
 * - 4.1: Display a toggle switch labeled "Customized Center"
 * - 4.2: When toggle is disabled, render standard QR code without center modification
 * - 4.5: When toggle is enabled, reveal additional center customization options
 */

import { useCallback, useId } from 'react';
import './CenterToggle.css';

/**
 * Props for the CenterToggle component
 */
export interface CenterToggleProps {
  /** Whether center customization is enabled */
  readonly enabled: boolean;
  /** Callback fired when the toggle state changes */
  readonly onChange: (enabled: boolean) => void;
}

/**
 * CenterToggle - Enable/disable center customization
 *
 * Provides a toggle switch with "Customized Center" label that controls
 * whether the QR code should have a customized center overlay area.
 * When enabled, additional options for logo and bay label become available.
 *
 * @example
 * ```tsx
 * <CenterToggle
 *   enabled={centerEnabled}
 *   onChange={(enabled) => setCenterEnabled(enabled)}
 * />
 * ```
 */
export function CenterToggle({ enabled, onChange }: CenterToggleProps): JSX.Element {
  const inputId = useId();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.checked);
    },
    [onChange]
  );

  return (
    <div className="center-toggle">
      <label className="center-toggle__container" htmlFor={inputId}>
        <span className="center-toggle__label">Customized Center</span>
        <div className="center-toggle__switch-wrapper">
          <input
            id={inputId}
            type="checkbox"
            className="center-toggle__input"
            checked={enabled}
            onChange={handleChange}
            role="switch"
            aria-checked={enabled}
          />
          <span className="center-toggle__switch" aria-hidden="true">
            <span className="center-toggle__switch-thumb" />
          </span>
        </div>
      </label>
      <p className="center-toggle__description">
        {enabled
          ? 'Center overlay enabled. Configure logo and bay label options below.'
          : 'Enable to add a customized center area with logo or bay label.'}
      </p>
    </div>
  );
}
